/**
 * `@`-reference attachment menu for the AHP-based ChatComposer.
 *
 * Pressing `@` in the composer opens a multi-step keyboard-accessible menu:
 *   categories → repositories → files | issues | pull requests | discussions
 *
 * On final selection the `@query` trigger text is removed from the input and an
 * AHP attachment token is added — no @mention string is left in the prompt.
 *
 * Works in both embedded copilot-chat-app and immersive ChatApp surfaces because
 * it carries no dependency on CopilotChatManagerContext.
 */

import {InlineAutocomplete} from '@github-ui/inline-autocomplete'
import type {
  SelectSuggestionsEvent,
  ShowSuggestionsEvent,
  Suggestion,
  Suggestions,
  TextInputCompatibleChild,
} from '@github-ui/inline-autocomplete/types'
import type {AutocompleteDiscussion, AutocompleteIssue, AutocompletePullRequest} from '@github-ui/copilot-chat-types'
import type {Repository} from '@github-ui/agent-sessions-interface/components/repo-picker/repo'
import {isRepositoryInScope} from '@github-ui/agent-sessions-interface/components/repo-picker/scope'
import {useQuery} from '@github-ui/react-query'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {
  ArrowRightIcon,
  CommentDiscussionIcon,
  DiscussionClosedIcon,
  FileDirectoryFillIcon,
  FileIcon,
  GitPullRequestClosedIcon,
  GitPullRequestDraftIcon,
  GitPullRequestIcon,
  IssueClosedIcon,
  IssueDraftIcon,
  IssueOpenedIcon,
  RepoIcon,
} from '@primer/octicons-react'
import {ActionList, type ActionListItemProps} from '@primer/react'
import {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react'
import type React from 'react'

import {fetchAutocompleteDiscussions, fetchAutocompleteIssues, fetchAutocompletePulls} from './fetch-autocomplete'
import type {FilePickerSelection} from './FilePickerPanel'
import {RestrictionShield} from './RestrictionShield'
import {useRepoFiles} from '../repos/use-repo-files'

// ─────────────────────────────────────────────────────────────────────────────
// Step state types
// ─────────────────────────────────────────────────────────────────────────────

type EntityKind = 'files' | 'issues' | 'pulls' | 'discussions'

type AutocompleteStep =
  | {kind: 'categories'; filter: string}
  | {kind: 'repositories'; filter: string; nextStep?: EntityKind}
  | {kind: 'files'; repo: Repository; filter: string}
  | {kind: 'issues'; repo: Repository; filter: string}
  | {kind: 'pulls'; repo: Repository; filter: string}
  | {kind: 'discussions'; repo: Repository; filter: string}

// ─────────────────────────────────────────────────────────────────────────────
// Static category data
// ─────────────────────────────────────────────────────────────────────────────

const TOP_LEVEL_CATEGORIES = [
  {name: 'Repositories', icon: RepoIcon, key: 'repositories'},
  {name: 'Files and folders', icon: FileIcon, key: 'repositories:files'},
  {name: 'Issues', icon: IssueOpenedIcon, key: 'repositories:issues'},
  {name: 'Pull requests', icon: GitPullRequestIcon, key: 'repositories:pulls'},
  {name: 'Discussions', icon: CommentDiscussionIcon, key: 'repositories:discussions'},
] as const

type TopLevelCategoryKey = (typeof TOP_LEVEL_CATEGORIES)[number]['key']

function isCategoryKey(key: string): key is TopLevelCategoryKey {
  return TOP_LEVEL_CATEGORIES.some(c => c.key === key)
}

function categoryKeyToStep(key: TopLevelCategoryKey): AutocompleteStep {
  switch (key) {
    case 'repositories':
      return {kind: 'repositories', filter: ''}
    case 'repositories:files':
      return {kind: 'repositories', filter: '', nextStep: 'files'}
    case 'repositories:issues':
      return {kind: 'repositories', filter: '', nextStep: 'issues'}
    case 'repositories:pulls':
      return {kind: 'repositories', filter: '', nextStep: 'pulls'}
    case 'repositories:discussions':
      return {kind: 'repositories', filter: '', nextStep: 'discussions'}
  }
}

const ENTITY_NAMES: Record<EntityKind, string> = {
  files: 'Files and folders',
  issues: 'Issues',
  pulls: 'Pull requests',
  discussions: 'Discussions',
}

/** Returns the `title` breadcrumb shown above the autocomplete overlay. */
function getTitleForStep(step: AutocompleteStep | null): string | undefined {
  if (!step) return undefined
  switch (step.kind) {
    case 'categories':
      return undefined
    case 'repositories':
      return step.nextStep ? `${ENTITY_NAMES[step.nextStep]} ›` : 'Repositories ›'
    case 'files':
      return `Files and folders › ${step.repo.name} ›`
    case 'issues':
      return `Issues › ${step.repo.name} ›`
    case 'pulls':
      return `Pull requests › ${step.repo.name} ›`
    case 'discussions':
      return `Discussions › ${step.repo.name} ›`
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Minimal per-component debounce (avoids adding a hook package dep)
// ─────────────────────────────────────────────────────────────────────────────

function useDebouncedValue<T>(value: T, delayMs = 200): T {
  const [debounced, setDebounced] = useState<T>(value)
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(id)
  }, [value, delayMs])
  return debounced
}

// ─────────────────────────────────────────────────────────────────────────────
// URL builder for entity selections
// ─────────────────────────────────────────────────────────────────────────────

const ENTITY_PATH_SEGMENT: Record<Exclude<EntityKind, 'files'>, string> = {
  issues: 'issues',
  pulls: 'pull',
  discussions: 'discussions',
}

function buildEntityUrl(nwo: string, kind: Exclude<EntityKind, 'files'>, number: number): string {
  const origin = ssrSafeLocation.origin || 'https://github.com'
  return `${origin}/${nwo}/${ENTITY_PATH_SEGMENT[kind]}/${number}`
}

// ─────────────────────────────────────────────────────────────────────────────
// Inline suggestion renderers (no dependency on copilot-chat internals)
// ─────────────────────────────────────────────────────────────────────────────

function CategoryItem({name, icon: Icon, ...props}: {name: string; icon: React.ElementType} & ActionListItemProps) {
  return (
    <ActionList.Item aria-haspopup="menu" {...props}>
      <ActionList.LeadingVisual>
        <Icon />
      </ActionList.LeadingVisual>
      {name}
      <ActionList.TrailingVisual>
        <ArrowRightIcon />
      </ActionList.TrailingVisual>
    </ActionList.Item>
  )
}

function RepoItem({nwo, isMultistep, ...props}: {nwo: string; isMultistep: boolean} & ActionListItemProps) {
  if (isMultistep) {
    return (
      <ActionList.Item aria-haspopup="menu" {...props}>
        <ActionList.LeadingVisual>
          <RepoIcon />
        </ActionList.LeadingVisual>
        {nwo}
        <ActionList.TrailingVisual>
          <ArrowRightIcon />
        </ActionList.TrailingVisual>
      </ActionList.Item>
    )
  }
  return (
    <ActionList.Item {...props}>
      <ActionList.LeadingVisual>
        <RepoIcon />
      </ActionList.LeadingVisual>
      {nwo}
    </ActionList.Item>
  )
}

function FileItem({path, isDirectory, ...props}: {path: string; isDirectory: boolean} & ActionListItemProps) {
  const parts = path.split('/')
  const name = parts[parts.length - 1] ?? path
  return (
    <ActionList.Item {...props}>
      <ActionList.LeadingVisual>{isDirectory ? <FileDirectoryFillIcon /> : <FileIcon />}</ActionList.LeadingVisual>
      {name}
      {parts.length > 1 && (
        <ActionList.Description variant="inline">{parts.slice(0, -1).join('/')}/</ActionList.Description>
      )}
    </ActionList.Item>
  )
}

function IssueItem({issue, ...props}: {issue: AutocompleteIssue} & ActionListItemProps) {
  return (
    <ActionList.Item {...props}>
      <ActionList.LeadingVisual>
        {issue.state === 'open' && <IssueOpenedIcon aria-label="Open" className="fgColor-open" />}
        {issue.state === 'closed' && <IssueClosedIcon aria-label="Closed" className="fgColor-done" />}
        {issue.state === 'draft' && <IssueDraftIcon aria-label="Draft" className="fgColor-draft" />}
      </ActionList.LeadingVisual>
      {issue.title}
      <ActionList.Description>#{issue.number}</ActionList.Description>
    </ActionList.Item>
  )
}

function PullItem({pull, ...props}: {pull: AutocompletePullRequest} & ActionListItemProps) {
  return (
    <ActionList.Item {...props}>
      <ActionList.LeadingVisual>
        {pull.state === 'open' && <GitPullRequestIcon aria-label="Open" className="fgColor-open" />}
        {pull.state === 'closed' && <GitPullRequestClosedIcon aria-label="Closed" className="fgColor-done" />}
        {pull.state === 'draft' && <GitPullRequestDraftIcon aria-label="Draft" className="fgColor-draft" />}
      </ActionList.LeadingVisual>
      {pull.title}
      <ActionList.Description>#{pull.number}</ActionList.Description>
    </ActionList.Item>
  )
}

function DiscussionItem({discussion, ...props}: {discussion: AutocompleteDiscussion} & ActionListItemProps) {
  return (
    <ActionList.Item {...props}>
      <ActionList.LeadingVisual>
        {discussion.state === 'open' && <CommentDiscussionIcon aria-label="Open" className="fgColor-open" />}
        {discussion.state === 'closed' && <DiscussionClosedIcon aria-label="Closed" className="fgColor-muted" />}
      </ActionList.LeadingVisual>
      {discussion.title}
      <ActionList.Description>#{discussion.number}</ActionList.Description>
    </ActionList.Item>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────────────────────

export interface ChatReferenceAutocompleteProps {
  /** The textarea element from ChatComposer (passed via renderInput). */
  children: React.ReactElement
  /** Keeps the input wrapper mounted while disabling reference suggestions. */
  disabled?: boolean
  /** Imperative repository loader backed by TanStack Query. */
  repositoryLoader: (query: string) => Promise<Repository[]>
  scopeOwner?: string | null
  /** Currently attached repositories (used to prevent duplicates). */
  selectedRepos: Repository[]
  /** Called when a repository is attached from the menu. */
  onUpdateRepos: (repos: Repository[]) => void
  /** Called when a file or directory is attached from the menu. */
  onFilePickerSelect: (selections: FilePickerSelection[]) => void
  /**
   * Called when an issue, pull request, or discussion is selected.
   * Receives a same-origin GitHub URL; the caller handles AHP conversion,
   * deduplication, enrichment, and announcement.
   */
  attachReferenceUrl: (url: string) => void
  /** Portal container name forwarded to the suggestions overlay. */
  portalContainerName?: string
  /**
   * When set, an owner scope is active: the repositories heading shows a
   * {@link RestrictionShield} naming this owner, since private repository
   * references are limited to it (github-ui#31409). Consumers pass the active
   * session/scope owner login; omit it when no owner restriction applies.
   */
  restrictionOwner?: string
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export function ChatReferenceAutocomplete({
  children,
  disabled = false,
  repositoryLoader,
  scopeOwner,
  selectedRepos,
  onUpdateRepos,
  onFilePickerSelect,
  attachReferenceUrl,
  portalContainerName,
  restrictionOwner,
}: ChatReferenceAutocompleteProps) {
  const {isBrowser} = useRuntimeEnvironment()

  // Live autocomplete navigation state. `null` means the menu is closed.
  const [step, setStep] = useState<AutocompleteStep | null>(null)
  const [wasDisabled, setWasDisabled] = useState(disabled)
  if (disabled !== wasDisabled) {
    setWasDisabled(disabled)
    if (disabled) setStep(null)
  }

  // Stable map from nwo → Repository for selection look-ups without prop drilling.
  const reposMapRef = useRef<Map<string, Repository>>(new Map())

  // Repository query: active only while the repositories step is visible.
  const repoFilter = step?.kind === 'repositories' ? step.filter : ''
  const repoQuery = useQuery<Repository[]>({
    queryKey: ['ref-autocomplete-repos', scopeOwner !== undefined, scopeOwner ?? null, repoFilter],
    queryFn: () => repositoryLoader(repoFilter),
    enabled: !disabled && step?.kind === 'repositories' && scopeOwner !== null,
    staleTime: 60_000,
    placeholderData: prev => prev,
  })
  useLayoutEffect(() => {
    if (repoQuery.data) {
      reposMapRef.current.clear()
      for (const repo of repoQuery.data) {
        if (isRepositoryInScope(repo, scopeOwner)) reposMapRef.current.set(repo.name, repo)
      }
    }
  }, [repoQuery.data, scopeOwner])
  // Clear the map on unmount to release repo references and avoid memory leaks.
  useEffect(() => {
    const ref = reposMapRef.current
    return () => {
      ref.clear()
    }
  }, [])

  // Entity repo and filter: only meaningful for files/issues/pulls/discussions steps.
  const entityStep = step && step.kind !== 'categories' && step.kind !== 'repositories' ? step : null
  const entityRepo = entityStep?.repo && isRepositoryInScope(entityStep.repo, scopeOwner) ? entityStep.repo : undefined
  const entityNwo = entityRepo?.name ?? ''
  const entityFilter = entityStep?.filter ?? ''
  const debouncedEntityFilter = useDebouncedValue(entityFilter)

  // File tree query.
  const filesQuery = useRepoFiles(!disabled && step?.kind === 'files' ? entityRepo : undefined)
  const directoriesSet = useMemo(() => new Set(filesQuery.data?.directories ?? []), [filesQuery.data?.directories])

  // Issues query.
  const issuesQuery = useQuery<AutocompleteIssue[]>({
    queryKey: ['ref-autocomplete-issues', entityNwo, debouncedEntityFilter],
    queryFn: ({signal}) => fetchAutocompleteIssues(entityNwo, debouncedEntityFilter, signal),
    enabled: !disabled && step?.kind === 'issues' && !!entityNwo,
    staleTime: 30_000,
    placeholderData: prev => prev,
  })

  // Pull requests query.
  const pullsQuery = useQuery<AutocompletePullRequest[]>({
    queryKey: ['ref-autocomplete-pulls', entityNwo, debouncedEntityFilter],
    queryFn: ({signal}) => fetchAutocompletePulls(entityNwo, debouncedEntityFilter, signal),
    enabled: !disabled && step?.kind === 'pulls' && !!entityNwo,
    staleTime: 30_000,
    placeholderData: prev => prev,
  })

  // Discussions query.
  const discussionsQuery = useQuery<AutocompleteDiscussion[]>({
    queryKey: ['ref-autocomplete-discussions', entityNwo, debouncedEntityFilter],
    queryFn: ({signal}) => fetchAutocompleteDiscussions(entityNwo, debouncedEntityFilter, signal),
    enabled: !disabled && step?.kind === 'discussions' && !!entityNwo,
    staleTime: 30_000,
    placeholderData: prev => prev,
  })

  // Build the Suggestions array from the current deferred step.
  const suggestions = useMemo<Suggestions | null>(() => {
    if (!step) return null

    switch (step.kind) {
      case 'categories': {
        const f = step.filter.toLowerCase()
        const items = TOP_LEVEL_CATEGORIES.filter(cat => f === '' || cat.name.toLowerCase().includes(f))
        if (items.length === 0) return []
        return items.map<Suggestion>(cat => ({
          value: null,
          key: cat.key,
          render: props => <CategoryItem name={cat.name} icon={cat.icon} {...props} />,
        }))
      }

      case 'repositories': {
        if (repoQuery.isLoading && !repoQuery.isPlaceholderData) return 'loading'
        const repos = (repoQuery.data ?? []).filter(repo => isRepositoryInScope(repo, scopeOwner))
        const f = step.filter.toLowerCase()
        const filtered = f ? repos.filter(r => r.name.toLowerCase().includes(f)) : repos
        const isMultistep = !!step.nextStep
        if (filtered.length === 0) return []
        return filtered.map<Suggestion>(repo => ({
          value: isMultistep ? null : repo.name,
          key: repo.name,
          render: props => <RepoItem nwo={repo.name} isMultistep={isMultistep} {...props} />,
        }))
      }

      case 'files': {
        if (filesQuery.isLoading) return 'loading'
        const allPaths = [...(filesQuery.data?.paths ?? []), ...(filesQuery.data?.directories ?? [])]
        const f = step.filter.toLowerCase()
        const filtered = f ? allPaths.filter(p => p.toLowerCase().includes(f)) : allPaths
        const limited = filtered.slice(0, 8)
        if (limited.length === 0) return []
        return limited.map<Suggestion>(path => ({
          value: path,
          key: path,
          render: props => <FileItem path={path} isDirectory={directoriesSet.has(path)} {...props} />,
        }))
      }

      case 'issues': {
        if (issuesQuery.isLoading && !issuesQuery.isPlaceholderData) return 'loading'
        const issues = issuesQuery.data ?? []
        if (issues.length === 0) return []
        return issues.map<Suggestion>(issue => ({
          value: buildEntityUrl(entityNwo, 'issues', issue.number),
          key: `issue-${issue.number}`,
          render: props => <IssueItem issue={issue} {...props} />,
        }))
      }

      case 'pulls': {
        if (pullsQuery.isLoading && !pullsQuery.isPlaceholderData) return 'loading'
        const pulls = pullsQuery.data ?? []
        if (pulls.length === 0) return []
        return pulls.map<Suggestion>(pull => ({
          value: buildEntityUrl(entityNwo, 'pulls', pull.number),
          key: `pull-${pull.number}`,
          render: props => <PullItem pull={pull} {...props} />,
        }))
      }

      case 'discussions': {
        if (discussionsQuery.isLoading && !discussionsQuery.isPlaceholderData) return 'loading'
        const discussions = discussionsQuery.data ?? []
        if (discussions.length === 0) return []
        return discussions.map<Suggestion>(discussion => ({
          value: buildEntityUrl(entityNwo, 'discussions', discussion.number),
          key: `discussion-${discussion.number}`,
          render: props => <DiscussionItem discussion={discussion} {...props} />,
        }))
      }
    }
  }, [
    step,
    repoQuery.data,
    repoQuery.isLoading,
    repoQuery.isPlaceholderData,
    filesQuery.data,
    filesQuery.isLoading,
    directoriesSet,
    issuesQuery.data,
    issuesQuery.isLoading,
    issuesQuery.isPlaceholderData,
    pullsQuery.data,
    pullsQuery.isLoading,
    pullsQuery.isPlaceholderData,
    discussionsQuery.data,
    discussionsQuery.isLoading,
    discussionsQuery.isPlaceholderData,
    entityNwo,
    scopeOwner,
  ])

  // Update filter when the user types; initialise to categories on first trigger.
  const onShowSuggestions = useCallback((event: ShowSuggestionsEvent) => {
    setStep(prev => (prev ? {...prev, filter: event.query} : {kind: 'categories', filter: event.query}))
  }, [])

  // Close menu.
  const onHideSuggestions = useCallback(() => {
    setStep(null)
  }, [])

  const onSelectSuggestion = useCallback(
    ({suggestion}: SelectSuggestionsEvent) => {
      const key = typeof suggestion === 'object' ? suggestion.key : undefined

      if (typeof suggestion === 'object' && suggestion.value === null) {
        // Multistep item: transition to the next navigation level.
        const currentStep = step
        if (currentStep?.kind === 'categories' && key && isCategoryKey(key)) {
          setStep(categoryKeyToStep(key))
        } else if (currentStep?.kind === 'repositories' && currentStep.nextStep && key) {
          const repo = reposMapRef.current.get(key)
          if (repo && isRepositoryInScope(repo, scopeOwner)) setStep({kind: currentStep.nextStep, repo, filter: ''})
        }
        return
      }

      // Final selection: attach the reference and let shouldReplaceText clear the trigger text.
      const value = typeof suggestion === 'string' ? suggestion : suggestion.value
      if (!value) return

      const currentStep = step
      if (currentStep?.kind === 'repositories') {
        // Attach as a repository chip.
        const repo = reposMapRef.current.get(value)
        if (repo && isRepositoryInScope(repo, scopeOwner) && !selectedRepos.some(r => r.id === repo.id)) {
          onUpdateRepos([...selectedRepos, repo])
        }
      } else if (currentStep?.kind === 'files') {
        if (!isRepositoryInScope(currentStep.repo, scopeOwner)) return
        onFilePickerSelect([
          {
            repo: currentStep.repo,
            path: value,
            type: directoriesSet.has(value) ? 'directory' : 'file',
            ref: filesQuery.data?.ref,
            commitOID: filesQuery.data?.commitOID,
          },
        ])
      } else if (
        currentStep?.kind === 'issues' ||
        currentStep?.kind === 'pulls' ||
        currentStep?.kind === 'discussions'
      ) {
        if (isRepositoryInScope(currentStep.repo, scopeOwner)) attachReferenceUrl(value)
      }
    },
    [
      step,
      selectedRepos,
      onUpdateRepos,
      onFilePickerSelect,
      attachReferenceUrl,
      directoriesSet,
      scopeOwner,
      filesQuery.data,
    ],
  )

  /**
   * For null (multistep) suggestions InlineAutocomplete keeps the `@` trigger char
   * so the menu can re-open at the next step. For all other (final) suggestions we
   * return false so the `@query` text is removed instead of replaced.
   */
  const shouldReplaceText = useCallback(
    ({suggestion}: SelectSuggestionsEvent) => typeof suggestion === 'object' && suggestion.value === null,
    [],
  )

  // SSR / non-browser: render the textarea directly.
  if (!isBrowser) return children

  return (
    <InlineAutocomplete
      fullWidth
      triggers={[
        ...(disabled
          ? []
          : [
              {
                triggerChar: '@',
                multiWord: true,
                keepTriggerCharOnCommit: false,
                insertSpaceOnCommit: false,
              },
            ]),
      ]}
      suggestions={disabled ? null : suggestions}
      onShowSuggestions={onShowSuggestions}
      onHideSuggestions={onHideSuggestions}
      onSelectSuggestion={onSelectSuggestion}
      shouldReplaceText={shouldReplaceText}
      suggestionsPlacement="below"
      title={getTitleForStep(step)}
      titleTrailingVisual={
        step?.kind === 'repositories' && !step.nextStep && restrictionOwner ? (
          <RestrictionShield owner={restrictionOwner} />
        ) : undefined
      }
      asMenu
      portalName={portalContainerName}
    >
      {children as TextInputCompatibleChild}
    </InlineAutocomplete>
  )
}
